import {
  IArnoDecisionTemplate,
  IArnoTemplateAssignment,
} from '../../hooks/reactQuerys/ArnoAdminQueries';

/**
 * Auswahl der Rechtsgeschäftsarten für einen Entscheidungsbaum (#1245).
 *
 * Reine Funktionen, weil hier die eigentliche Aussage steckt: was beim Speichern
 * an das Backend geht, was der Filter „nur ausgewählte" zeigt und was passiert,
 * wenn jemand die Vorgabe umsetzt. Bei hunderten Rechtsgeschäftsarten ist das
 * nichts, was man sich durchklicken möchte.
 */

/** Was zu einer ausgewählten Rechtsgeschäftsart gemerkt wird. */
export interface ISelectionEntry {
  /** Vorauswahl bei der Urkundenerzeugung; je Rechtsgeschäftsart nur einmal. */
  isDefault: boolean;
  /** Ausgesetzt heißt: Verbindung bleibt, wird aber nicht angeboten. */
  isEnabled: boolean;
}

export type Selection = Record<number, ISelectionEntry>;

/** Eine Rechtsgeschäftsart, so wie DiNo sie liefert. */
export interface ITypeOption {
  idLegalTransactionType: number;
  LegalTransactionType: string;
}

/** Der gespeicherte Stand eines Baums als Ausgangspunkt der Auswahl. */
export const initialSelection = (
  template: IArnoDecisionTemplate | null
): Selection =>
  Object.fromEntries(
    (template?.assignments ?? []).map(entry => [
      entry.idLegalTransactionType,
      { isDefault: entry.isDefault, isEnabled: entry.isEnabled },
    ])
  );

/**
 * Auswählen und wieder abwählen.
 *
 * Abwählen wirft den Eintrag weg statt ihn zu deaktivieren: eine entfernte
 * Freigabe ist keine ausgesetzte, und beide Zustände auseinanderzuhalten ist der
 * Sinn der Unterscheidung.
 */
export const toggleSelection = (
  selection: Selection,
  idLegalTransactionType: number
): Selection => {
  const next = { ...selection };
  if (next[idLegalTransactionType]) {
    delete next[idLegalTransactionType];
  } else {
    next[idLegalTransactionType] = { isDefault: false, isEnabled: true };
  }
  return next;
};

/**
 * Vorgabe setzen oder zurücknehmen.
 *
 * Nur für eine bereits ausgewählte Rechtsgeschäftsart, und eine Vorgabe ist
 * immer auch freigegeben — eine ausgesetzte Vorauswahl wäre ein Widerspruch.
 */
export const toggleDefault = (
  selection: Selection,
  idLegalTransactionType: number
): Selection => {
  const entry = selection[idLegalTransactionType];
  if (!entry) {
    return selection;
  }
  return {
    ...selection,
    [idLegalTransactionType]: { isEnabled: true, isDefault: !entry.isDefault },
  };
};

/** Was in der Liste steht: Suche und „nur ausgewählte" greifen zusammen. */
export const visibleTypes = (
  types: ITypeOption[],
  selection: Selection,
  search: string,
  onlySelected: boolean
): ITypeOption[] => {
  const needle = search.trim().toLowerCase();
  return types.filter(type => {
    if (onlySelected && !selection[type.idLegalTransactionType]) {
      return false;
    }
    return !needle || type.LegalTransactionType.toLowerCase().includes(needle);
  });
};

/**
 * Die Nutzlast für das Speichern: die vollständige Zielmenge.
 *
 * Das Backend bringt die Freigaben auf genau diese Menge, deshalb bedeutet ein
 * fehlender Eintrag „entfernen". Einmal speichern statt zwanzig Anfragen.
 */
export const assignmentsPayload = (
  selection: Selection
): {
  idLegalTransactionType: number;
  isDefault: boolean;
  isEnabled: boolean;
}[] =>
  Object.entries(selection)
    .map(([id, entry]) => ({
      idLegalTransactionType: Number(id),
      isDefault: entry.isDefault,
      isEnabled: entry.isEnabled,
    }))
    .sort((a, b) => a.idLegalTransactionType - b.idLegalTransactionType);

/** Was sich gegenüber dem gespeicherten Stand ändern würde. */
export const selectionChanges = (
  stored: IArnoTemplateAssignment[],
  selection: Selection
): { added: number; removed: number; changed: number } => {
  const before = new Map(
    stored.map(entry => [entry.idLegalTransactionType, entry])
  );
  let added = 0;
  let changed = 0;
  Object.entries(selection).forEach(([id, entry]) => {
    const previous = before.get(Number(id));
    if (!previous) {
      added += 1;
    } else if (
      previous.isDefault !== entry.isDefault ||
      previous.isEnabled !== entry.isEnabled
    ) {
      changed += 1;
    }
  });
  const removed = stored.filter(
    entry => !selection[entry.idLegalTransactionType]
  ).length;
  return { added, removed, changed };
};
